import { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { IDepartureMessage, IArrivalMessage } from "@vmosolutions/oc-interfaces";
import { Grid, Typography, styled, alpha } from "@mui/material";
import { EventComment } from "../../CommentEditor/EventComment";
import { formatDateString } from "src/components/DateDisplay";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface ISummaryItem {
  /** The key to reference this particular item */
  key: string;
  /** Data to display */
  value: string;
  /** A visually appealing way of displaying the Key */
  label?: string;
}

const OCDisplayMessages = styled(Grid)(({ theme }) => ({
  display: "block",

  "& .message_container": {
    "& .event_comment": {
      margin: "0px 0px 10px 0px",
    },
  },

  "& .form": {
    padding: 5,
    overflow: "auto",
    display: "flex",
    "& .form-items": {
      display: "flex",
      "& .submit": {
        color: theme.palette.primary.main,
        backgroundColor: "inherit",
        boxShadow: "none",
        alignSelf: "flex-end",
        "&:hover": {
          backgroundColor: alpha(theme.palette.background.default, 0.3),
        },
      },
    },
  },
}));

//turn the message into a single string to pass into the eventComment component
export const makeMessageAString = (messages: ISummaryItem[][]): string[] => {
  const result: string[] = messages.map((message: ISummaryItem[]) => {
    let tempString = "";
    message.forEach((item: ISummaryItem) => {
      tempString = tempString.concat(item.label, ":", item?.value, "\n");
    });
    return tempString;
  });
  return result;
};

export const convertObjectToStrings = (messages: ISummaryItem[][]): string[] => {
  const result: string[] = messages.map((message: ISummaryItem[]) => {
    let tempString = "";
    message.forEach((item: ISummaryItem) => {
      tempString = tempString.concat(item.label, ":", item?.value, "\n");
    });
    return tempString;
  });
  return result;
};

const timeKeys = ["actualOut", "actualOff", "actualOn", "actualIn", "ETA", "createdAt"];
/**
 * Recursively loop through custom objects and return label value pairs.
 * @param obj - custom object
 */
const flattenCustomObject = (obj: any, schemaProperties: any): any => {
  const res: { [key: string]: any } = {};
  let extendedKeyName = "";
  function recurse(obj: any): void {
    // console.log(obj);
    for (const key in obj) {
      const item = obj[key];
      // console.log("custom obj", obj);

      if (item) {
        if (item && typeof item === "object" && !item.unit) {
          // Only recurse if not a unit value type.
          extendedKeyName = extendedKeyName ? extendedKeyName + "." + key : key;
          // console.log(value);
          recurse(item);
        } else {
          const objectPath = extendedKeyName ? `${extendedKeyName}.${key}` : key;
          // Strip out certain words
          const newKey = objectPath.replace("custom.", "").replace(".value", "");
          const label = schemaProperties[key]?.title ?? key;
          let formatedValue = "undefined";

          switch (true) {
            case timeKeys.includes(key) && item > 0: // If keys are known time values, show as formatted date.
              formatedValue = formatDateString(item, "Etc/UTC");
              break;
            case item?.hasOwnProperty("value") && item.hasOwnProperty("unit"): // Custom field is a unitValue type.
              formatedValue = `${item?.value} ${item?.unit}`;
              break;
            default:
              formatedValue = item ?? "undefined";
            // code block
          }
          res[newKey] = { label, value: formatedValue };
          extendedKeyName = "";
        }
      }
    }
  }
  recurse(obj);
  return res;
};

interface IFlattedCustom {
  [key: string]: { label: string; value: string };
}

// Converts the flatted custom object into a string.
const customToString = (obj: IFlattedCustom): string => {
  let tempString = "";
  for (const [key, item] of Object.entries(obj)) {
    if (key && item) {
      const { label, value } = item;
      tempString = tempString?.concat(label, ": ", value?.toString(), "\n");
    }
  }
  return tempString;
};

interface IDepartureMessageWithType extends IDepartureMessage {
  type?: "Arrival" | "Departure";
  availableDate?: number; // Stores createdAt or receivedAt based on what's available
  customLabelsValues?: IFlattedCustom;
}
interface IArrivalMessageWithType extends IArrivalMessage {
  type?: "Arrival" | "Departure";
  availableDate?: number; // Stores createdAt or receivedAt based on what's available
  customLabelsValues?: IFlattedCustom;
}

/** Extend the IMessage interface so that it includes the labelValueObj */
type IMessage = IDepartureMessageWithType | IArrivalMessageWithType;
export interface IDisplayMessagesProps {
  flightId: string;
  departureMessages: IDepartureMessage[];
  arrivalMessages: IArrivalMessage[];
  windowId?: string;
}

export const sortByLatestReceived = (a: IMessage, b: IMessage): number => {
  return a.availableDate > b.availableDate ? -1 : 1;
};

export const DisplayMessages = (props: IDisplayMessagesProps): JSX.Element => {
  const { windowId, departureMessages, arrivalMessages } = props;
  const [allMessages, setAllMessages] = useState<IMessage[]>([]);

  // Return custom flight schema properties.
  const customFlightTabs = useSelector((state: IRootState) => state.customPages.flight);
  const schemaProperties = useMemo(() => {
    if (customFlightTabs?.length > 0) {
      const properties = customFlightTabs?.map((item) => {
        return item?.customSchema ? JSON.parse(item?.customSchema)?.properties : null;
      });
      // Spread out all nested properties into one top level object.
      return properties?.reduce((acc, item) => {
        acc = { ...acc, ...item };
        return acc;
      }, {});
    }
    return [];
  }, [customFlightTabs]);

  useEffect(() => {
    const departures: IMessage[] = departureMessages.map((d) => {
      return {
        ...d,
        type: "Departure",
        availableDate: d.createdAt
          ? new Date(d.createdAt)?.valueOf()
          : d.receivedAt
            ? new Date(d.receivedAt)?.valueOf()
            : 0,
        customLabelsValues: flattenCustomObject(d?.custom?.flight ? d?.custom.flight : d?.custom, schemaProperties),
      };
    });
    const arrivals: IMessage[] = arrivalMessages.map((a) => {
      return {
        ...a,
        type: "Arrival",
        availableDate: a.createdAt
          ? new Date(a.createdAt)?.valueOf()
          : a.receivedAt
            ? new Date(a.receivedAt)?.valueOf()
            : 0,
        customLabelsValues: flattenCustomObject(a?.custom?.flight ? a?.custom.flight : a?.custom, schemaProperties),
      };
    });
    const messages = [...departures, ...arrivals];
    const sortedMessages = messages?.length > 0 ? messages.sort(sortByLatestReceived) : [];
    const total = departureMessages?.length + arrivalMessages?.length;
    if (sortedMessages?.length === total) {
      setAllMessages(sortedMessages);
    }
    //return sortedMessages;
  }, [departureMessages, arrivalMessages, schemaProperties]);

  //HACK For some reason, the rendered list is causing the mosaic panel to scroll to it's vertical center
  //TODO Remove this hack once a more elegant fix has been discovered to resolve the scrolling bug
  useLayoutEffect(() => {
    if (allMessages?.length > 0) {
      const mosaicPane = document.getElementById(`${windowId}-panel`);
      if (mosaicPane) {
        setTimeout(() => {
          // Needs to be wrapped inside a setTimeout to work corrently.
          mosaicPane.scrollTo(0, 0);
        }, 0);
      }
    }
  }, [windowId, allMessages]);

  return (
    <OCDisplayMessages container direction="column">
      {allMessages?.length > 0 ? (
        allMessages.map((message: IMessage, index) => {
          const { availableDate, type, source, rawMessage, notes, customLabelsValues } = message;
          const messageType = `${type ?? ""} Message`;
          const receivedDate = availableDate > 0 ? `- Received ${formatDateString(availableDate, "Etc/UTC")}` : "";
          const messageHeader = `${source ?? ""} ${messageType} ${receivedDate}`;
          const customString =
            customLabelsValues && Object.keys(customLabelsValues).length > 0
              ? `${customToString(customLabelsValues)} \n`
              : "";
          const messageString = rawMessage ? `Raw Message: \n ${rawMessage} \n` : "";
          const notesString = notes ? ` Notes: \n ${notes} \n` : "";

          return (
            <Grid key={index} item className="message" style={{ display: "flex", height: "auto", margin: "5px 8px" }}>
              <EventComment
                user={messageHeader}
                index={index}
                content={customString + messageString + notesString + "\n"}
              />
            </Grid>
          );
        })
      ) : (
        <Grid item style={{ margin: "5px 15px" }}>
          <Typography variant="body2">No Messages to Display</Typography>
        </Grid>
      )}
    </OCDisplayMessages>
  );
};
